import React from "react";
import NavBar from "../components/Navbar/NavBar";
import Footer from "../components/Footer";
import { useDocTitle } from "../components/CustomHook";
import { Link } from "react-router-dom";

/*
 * Hardware Assistance Program — vision + interest list page.
 * HONESTY: the program is in development. We do NOT claim applications
 * are open, do not promise specific items, and do not take donations
 * earmarked for hardware until the program's accounting exists.
 */

const offerings = [
  {
    icon: "💽",
    title: "Refurbished Storage",
    desc: "Free SSDs and hard drives (safely wiped, tested) for members whose computers are failing or too slow.",
  },
  {
    icon: "🎧",
    title: "Peripherals & Accessories",
    desc: "Headphones, keyboards, mice, cables, and webcams — the small things that make a computer usable.",
  },
  {
    icon: "💻",
    title: "Computer Vouchers",
    desc: "Vouchers toward the purchase of a laptop or desktop, for members with the greatest need.",
  },
];

const steps = [
  {
    n: "1",
    title: "Become a member",
    desc: "Membership is free — it's how we verify who we're helping.",
  },
  {
    n: "2",
    title: "Tell us what you need",
    desc: "A short application describing your situation and what hardware would help.",
  },
  {
    n: "3",
    title: "Review by our team",
    desc: "Applications are reviewed by real people, prioritized by need.",
  },
  {
    n: "4",
    title: "Pickup or delivery",
    desc: "Tested equipment with setup help from our team.",
  },
];

const HardwareAssistance = () => {
  useDocTitle("ZelT | Hardware Assistance");

  return (
    <div>
      <NavBar />
      <div className="mt-8 w-full bg-white py-12 lg:py-20">
        <div className="mx-4 max-w-5xl mx-auto">
          {/* hero */}
          <div className="text-center mb-12" data-aos="zoom-in">
            <span className="inline-block px-4 py-1 rounded-full text-xs font-bold bg-purple-100 text-blue-900 border border-purple-300 mb-4">
              IN DEVELOPMENT · NOT YET ACCEPTING APPLICATIONS
            </span>
            <h1 className="text-4xl md:text-5xl font-extrabold text-blue-900 mb-4">
              Hardware Assistance Program
            </h1>
            <p className="text-lg text-gray-600 max-w-3xl mx-auto leading-relaxed">
              Sometimes the barrier isn't knowledge — it's hardware. A dying
              hard drive, broken headphones, or no computer at all. We're
              building a program to get real equipment into the hands of
              members who need it.
            </p>
          </div>

          {/* what we plan to offer */}
          <h2 className="text-2xl font-extrabold text-blue-900 mb-6 text-center" data-aos="fade-up">
            What We Plan to Offer
          </h2>
          <div className="grid gap-6 md:grid-cols-3 mb-12">
            {offerings.map((o) => (
              <div
                key={o.title}
                className="bg-gray-50 rounded-2xl p-6 shadow-lg border-t-4 border-purple-300"
                data-aos="fade-up"
              >
                <span className="text-4xl mb-3 block">{o.icon}</span>
                <h3 className="font-extrabold text-blue-900 mb-2">{o.title}</h3>
                <p className="text-gray-600 text-sm leading-relaxed">{o.desc}</p>
              </div>
            ))}
          </div>

          {/* how it will work */}
          <h2 className="text-2xl font-extrabold text-blue-900 mb-6 text-center" data-aos="fade-up">
            How It Will Work
          </h2>
          <div className="grid gap-5 sm:grid-cols-2 lg:grid-cols-4 mb-12">
            {steps.map((s) => (
              <div
                key={s.n}
                className="bg-white rounded-2xl p-6 shadow-md border-t-4 border-blue-900 text-center"
                data-aos="zoom-in"
              >
                <p className="w-10 h-10 mx-auto rounded-full bg-blue-900 text-white font-extrabold flex items-center justify-center mb-3">
                  {s.n}
                </p>
                <h3 className="font-bold text-blue-900 mb-2">{s.title}</h3>
                <p className="text-gray-600 text-sm leading-relaxed">{s.desc}</p>
              </div>
            ))}
          </div>

          {/* honesty block */}
          <div
            className="rounded-2xl p-8 text-white shadow-xl mb-12"
            style={{ background: "linear-gradient(90deg, #36395a, #1e3a8a)" }}
            data-aos="fade-up"
          >
            <h2 className="text-2xl font-extrabold mb-3">
              💡 Why "in development"?
            </h2>
            <p className="text-gray-300 leading-relaxed">
              Because trust matters more than hype. This program needs real
              funding (through campaigns like the LAMNA Launch Fund),
              donated equipment, and a fair review process before we accept
              our first application. When that's ready, this page changes
              first. If you need something urgent today, message us anyway —
              we'll do what we can.
            </p>
          </div>

          {/* CTA */}
          <div className="text-center" data-aos="fade-up">
            <h2 className="text-2xl font-extrabold text-blue-900 mb-3">
              Join the Interest List
            </h2>
            <p className="text-gray-600 mb-6 max-w-xl mx-auto">
              Tell us who you are and what you need — interest-list members
              are contacted first when applications open.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link
                to="/contact"
                className="inline-block px-6 py-3 rounded-xl bg-blue-900 text-white font-bold hover:bg-blue-800 transition"
              >
                📋 Join the Interest List
              </Link>
              <Link
                to="/membership"
                className="inline-block px-6 py-3 rounded-xl border-2 border-blue-900 text-blue-900 font-bold hover:bg-blue-50 transition"
              >
                Become a Free Member First
              </Link>
            </div>
            <p className="text-gray-400 text-xs mt-6 max-w-md mx-auto">
              Tip: mention "Hardware Assistance" in your message so it routes
              to the right place.
            </p>
          </div>
        </div>
      </div>
      <Footer />
    </div>
  );
};

export default HardwareAssistance;
